import { Component, Input, NgZone, OnInit } from '@angular/core';
import { ReviewPlanVerbose } from '../../models/review-plan.model';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SelectEmployeesDialogComponent } from '@app/shared/components/select-employees-dialog/select-employees-dialog.component';
import { ReviewPlansService } from '../../services/review-plans.service';
import { TranslateService } from '@ngx-translate/core';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { defer, finalize, forkJoin } from 'rxjs';
import { PageChangeEvent } from '@progress/kendo-angular-grid';

@Component({
  selector: 'app-review-plan-owners',
  templateUrl: './review-plan-owners.component.html',
  styleUrls: ['./review-plan-owners.component.scss']
})
export class ReviewPlanOwnersComponent implements OnInit {
  @Input() reviewPlan: ReviewPlanVerbose;

  isLoadingEmployees: boolean;
  employeesGridDataResult: { data: any[]; total: number; };
  pageSize: number = 5;
  skip: number = 0;
  public columns: any[] = [
    { field: "owner", title: this.translate.instant('ReviewPlanOwners-Name'), type:'employee' },
  ];
  clearSelectedItems: boolean = false;
  searchValue: string;
  searchFilterString: string;
  getUsersRequest: any;
  selectEmployeesDialogRef: MatDialogRef<SelectEmployeesDialogComponent>;

  constructor(
    private dialog: MatDialog,
    private ngZone: NgZone,
    private reviewPlansService: ReviewPlansService,
    public translate: TranslateService,
    private overlayService: OverlayService,
    private snackbarService: SnackbarService,
  ) { }

  ngOnInit(): void {
    this.getReviewPlanOwners();
  }

  getReviewPlanOwners() {
    this.isLoadingEmployees = true;

    let filter;
    
    if(this.searchFilterString) {
      filter = `${this.searchFilterString} AND ${filter}`
    }

    this.getUsersRequest = this.reviewPlansService.getReviewPlanOwners(this.reviewPlan.id, this.pageSize, this.skip, null, filter)
    .pipe(
      finalize( () => {
        this.isLoadingEmployees = false;
      })
    )
    .subscribe(
      (res) => {
        this.employeesGridDataResult = {
          data: res.data,
          total: res.total,
        }
      }
    );
  }

  search(newSearchString: string){
    this.searchValue = newSearchString;
    this.searchFilterString = '';
    this.skip = 0;

    let variants = this.searchValue.split(' ').filter(value => value);
    variants.map(variant => {
        if (variant) {
            if (this.searchFilterString.length > 2) {
                this.searchFilterString += ' AND ';
            }
            this.searchFilterString += `(Owner.FirstName like "${variant}") OR (Owner.LastName like "${variant}")`;
        }
    });

    this.getUsersRequest.unsubscribe();
    this.getReviewPlanOwners();
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.getReviewPlanOwners();
  }

  showAddUserModal() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      nonSelectableEmployees: this.employeesGridDataResult.data.map( reviewPlanOwner => reviewPlanOwner?.owner?.id )
    };

    this.ngZone.runOutsideAngular(() => {
      this.selectEmployeesDialogRef = this.dialog.open(SelectEmployeesDialogComponent, dialogConfig);
    });

    const sub = this.selectEmployeesDialogRef.componentInstance.emitSelectedEmployees.subscribe((event) => {
      this.addOwners(event)
    });
  }

  addOwners(selectedEmployees: string[]) {
    const observables = selectedEmployees.map(
      selectedUserId => defer(
        () => this.reviewPlansService.addReviewPlanOwner(this.reviewPlan.id, {userId: selectedUserId})
      )
    );

    this.overlayService.show();

    forkJoin(observables)
        .pipe(finalize(() => this.overlayService.hide()))
        .subscribe(
            (res) => {
                this.getReviewPlanOwners();
                this.selectEmployeesDialogRef.close();
                this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
            }
        );
  }

  deleteAllSelected(reviewPlanOwners: string[]) {
    const observables = reviewPlanOwners.map(reviewPlanOwner => defer(() => this.reviewPlansService.deleteReviewPlanOwner(this.reviewPlan.id, reviewPlanOwner)));

    this.overlayService.show();

    forkJoin(observables)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe(
          (res) => {
              this.clearSelectedItems = !this.clearSelectedItems;
              this.getReviewPlanOwners();
              this.snackbarService.openSnackBar(`${this.translate.instant('RemovedSuccessfully')}`, 'clear', 'success');
          }
      );
  }
}
